import React from "react";
import { cn } from "@/lib/utils";
import ResponsiveImage from "../ui/ResponsiveImage";
import { getIconUrl } from "@/lib/directus";
import { cva } from "class-variance-authority";
import { LazyMotion, domAnimation, m } from "framer-motion";

interface ContactBackgroundTextProps {
  data: {
    asset: any[];
    title: string;
    subtitle: string;
    texts: any[];
    textAlignment?: "left" | "center" | "right";
  };
}

// Styling variants (copied and adapted from BackgroundImageText)
const backgroundTextBoxVariants = cva(
  "relative w-full desktop:max-w-3/5 xl:max-w-2/5",
  {
    variants: {
      alignment: {
        left: ["w-full"],
        center: ["w-full", "desktop:left-1/2", "desktop:-translate-x-1/2"],
        right: ["w-full", "desktop:ml-auto", "desktop:mr-0"],
      },
    },
    defaultVariants: {
      alignment: "left",
    },
  }
);

const backgroundTextBoxStyleVariants = cva(
  "py-12 px-6 flex flex-col gap-6 md:gap-12 md:py-16 desktop:py-20 px-[7.5%]",
  {
    variants: {
      style: {
        dark: "bg-accent-color-three",
        light: "bg-accent-color-three-light",
      },
    },
    defaultVariants: {
      style: "dark",
    },
  }
);

const textSmallTitleVariants = cva("font-sans text-special-h2 uppercase", {
  variants: {
    style: {
      dark: "text-accent-color-two-light",
      light: "text-[#FFFFFF]",
    },
  },
  defaultVariants: {
    style: "dark",
  },
});

const textTitleVariants = cva(
  "font-serif text-h2-mobile md:text-h2-tablet desktop:text-h2-desktop",
  {
    variants: {
      style: {
        dark: "text-secondary-light",
        light: "text-[#FFFFFF]",
      },
    },
    defaultVariants: {
      style: "dark",
    },
  }
);

const textContentVariants = cva(
  [
    "font-sans font-light tracking-normal whitespace-pre-line",
    "text-paragraph-large",
  ],
  {
    variants: {
      style: {
        dark: "text-[#FAFAFA]",
        light: "text-[#FFFFFF]",
      },
    },
    defaultVariants: {
      style: "dark",
    },
  }
);

const containerTextBox = cva("w-full", {
  variants: {
    alignment: {
      left: "desktop:mr-auto",
      center: "desktop:mx-auto",
      right: "desktop:ml-auto",
    },
  },
  defaultVariants: {
    alignment: "left",
  },
});

export default function ContactBackgroundText({
  data: { asset, title, subtitle, texts, textAlignment = "left" },
}: ContactBackgroundTextProps) {
  return (
    <LazyMotion features={domAnimation}>
      <section
      className="relative w-full grid auto-rows-min"
      style={{
        display: "grid",
        gridTemplateAreas: "'stack'",
      }}
    >
      <div
        className="relative h-[50vh] desktop:static desktop:h-auto col-start-1 row-start-1 w-full"
        style={{ gridArea: "stack" }}
      >
        {asset.length > 0 && (
          <ResponsiveImage
            desktop_image={asset[0].item.desktop_image}
            tablet_image={asset[0].item.tablet_image}
            mobile_image={asset[0].item.mobile_image}
            alt={asset[0].item.imageAlt ?? title}
            className="z-0 w-full object-cover"
          />
        )}
      </div>

      <div
        className={cn(
          containerTextBox({ alignment: textAlignment }),
          "desktop:max-w-[85vw] desktop:mx-[7.5vw]"
        )}
      >
        <m.div
          className={cn(
            backgroundTextBoxVariants({ alignment: textAlignment }),
            "col-start-1 row-start-1 w-full"
          )}
          style={{ gridArea: "stack", zIndex: 2 }}
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.8, ease: "easeOut" }}
        >
          <div
            className={cn(
              backgroundTextBoxStyleVariants({ style: "dark" }),
              "desktop:mt-32 desktop:mb-32"
            )}
          >
            <div className="gap-2 flex flex-col">
              <m.p
                className={cn(textTitleVariants({ style: "dark" }))}
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ duration: 0.7, ease: "easeOut" }}
              >
                {title}
              </m.p>
              <m.p
                className={cn(textSmallTitleVariants({ style: "dark" }))}
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ duration: 0.7, ease: "easeOut" }}
              >
                {subtitle}
              </m.p>
            </div>
            <m.div
              className={cn(
                textContentVariants({ style: "dark" }),
                "flex flex-col gap-6 mt-6"
              )}
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 0.7, ease: "easeOut" }}
            >
              {texts.map((item, idx) => (
                <div key={idx} className="mb-4">
                  {item.item.title && (
                    <div className="font-sans text-secondary-light text-xl md:text-2xl mb-1">
                      {item.item.title}
                    </div>
                  )}
                  <div
                    className="font-sans text-[#FAFAFA] text-base md:text-lg whitespace-pre-line"
                    dangerouslySetInnerHTML={{ __html: item.item.text }}
                  ></div>
                </div>
              ))}
            </m.div>
          </div>
        </m.div>
      </div>
    </section>
    </LazyMotion>
  );
}
